import { useWallet } from '@solana/wallet-adapter-react';
import { VersionedTransaction } from '@solana/web3.js';
import { delay } from 'es-toolkit/promise';
import { Check } from 'lucide-react';
import { type ComponentProps, useState } from 'react';
import { toast } from 'sonner';
import { useTRPC } from '../hooks/useTRPC';
import { getErrorMessage } from '../utils/errorHandling';
import { captureAndReportError } from '../utils/errorReporting';
import { Button } from './Button';

async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  baseDelayMs = 1000,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries - 1) {
        await delay(baseDelayMs * 2 ** attempt);
      }
    }
  }
  throw lastError;
}

interface VerifyCollectionButtonProps {
  dappId: string;
  nftMintAddress: string;
  collectionMintAddress: string;
  collectionAuthority: string;
  verificationTarget?: 'app' | 'release';
  onSuccess?: () => void;
  className?: string;
  variant?: ComponentProps<typeof Button>['variant'];
  releaseId?: string;
}

export function VerifyCollectionButton({
  dappId,
  nftMintAddress,
  collectionMintAddress,
  collectionAuthority,
  verificationTarget = 'release',
  onSuccess,
  className = '',
  variant = 'outlineWhite',
  releaseId,
}: VerifyCollectionButtonProps) {
  const trpc = useTRPC();
  const { publicKey, signTransaction, connected } = useWallet();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleVerifyCollection = async () => {
    if (!publicKey || !signTransaction) {
      toast.error('Please connect your wallet');
      return;
    }

    if (publicKey.toBase58() !== collectionAuthority) {
      toast.error('Please connect with the wallet that owns this collection');
      return;
    }

    setLoading(true);

    try {
      toast.info('Preparing collection verification transaction...');

      const preparedTxResult =
        verificationTarget === 'app'
          ? await trpc.prepareVerifyAppCollectionTransaction.mutate({
              dappId,
              nftMintAddress,
              collectionMintAddress,
              collectionAuthority,
              payerAddress: publicKey.toBase58(),
            })
          : await trpc.release.prepareVerifyCollectionTransaction.mutate({
              dappId,
              nftMintAddress,
              collectionMintAddress,
              collectionAuthority,
              payerAddress: publicKey.toBase58(),
            });

      if (preparedTxResult._tag === 'Left') {
        throw new Error(
          preparedTxResult.left.message || 'Failed to prepare transaction',
        );
      }

      const { transaction: transactionBase64 } = preparedTxResult.right;

      const transactionBuffer = Buffer.from(transactionBase64, 'base64');
      const transaction = VersionedTransaction.deserialize(transactionBuffer);

      toast.info('Please sign the transaction in your wallet...');
      const signedTransaction = await signTransaction(transaction);

      toast.info('Submitting transaction...');

      const submitResult = await trpc.submitNftTransaction.mutate({
        signedTransaction: Buffer.from(signedTransaction.serialize()).toString(
          'base64',
        ),
      });

      if (submitResult._tag === 'Left') {
        throw new Error(
          submitResult.left.message || 'Failed to submit transaction',
        );
      }

      const signature = submitResult.right.transactionSignature;

      // On-chain TX succeeded; DB must be updated. Retry on transient failures.
      if (verificationTarget === 'release' && releaseId) {
        try {
          await retryWithBackoff(
            () =>
              trpc.release.markReleaseCollectionAsVerified.mutate({
                releaseId,
              }),
            3,
            1000,
          );
        } catch (markError) {
          captureAndReportError(
            'Failed to mark collection as verified in database after retries:',
            markError,
          );
          toast.warning(
            'Collection verified on-chain, but database update failed. ' +
              'The system will auto-recover on next submission attempt.',
            { duration: 8000 },
          );
        }
      }

      toast.success(
        <div>
          <p>Collection verified successfully!</p>
          <a
            href={`https://solana.fm/tx/${signature}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-teal-400 hover:text-teal-300 underline text-sm"
          >
            View transaction
          </a>
        </div>,
      );

      setSuccess(true);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to verify collection',
      );
      const isUserRejection = errorMessage
        .toLowerCase()
        .includes('user rejected');

      if (!isUserRejection) {
        captureAndReportError(error, {
          operation: 'Collection verification failed',
          component: 'VerifyCollectionButton',
          nft_type: verificationTarget,
          stage: 'verify_collection',
          dappId: dappId,
          nftMintAddress: nftMintAddress,
          collectionMintAddress: collectionMintAddress,
          collectionAuthority: collectionAuthority,
          releaseId: releaseId,
          walletConnected: connected,
          walletAddress: publicKey?.toBase58(),
          errorMessage: errorMessage,
        });
      }

      if (isUserRejection) {
        toast.error('Transaction cancelled');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!nftMintAddress || !collectionMintAddress || !collectionAuthority) {
    return null;
  }

  if (success) {
    return (
      <div className="flex items-center gap-2 text-green-400">
        <Check className="size-4" />
        <span className="text-sm">Collection Verified</span>
      </div>
    );
  }

  return (
    <Button
      onClick={handleVerifyCollection}
      disabled={
        loading ||
        !connected ||
        (connected && publicKey?.toBase58() !== collectionAuthority)
      }
      variant={variant}
      className={className}
    >
      {loading
        ? 'Verifying...'
        : !connected
          ? 'Connect Wallet to Verify'
          : publicKey?.toBase58() !== collectionAuthority
            ? 'Wrong Wallet Connected'
            : 'Verify Collection'}
    </Button>
  );
}
